import { isApiError } from '@keboola/api-client';

/**
 * apps-api reports failures in its own envelope — `{ errorMessage, errorType?, requestId? }`, the
 * `Error` schema of the generated developer-portal client — rather than the platform's shape, so the
 * message worth showing is inside `ApiError.data`.
 *
 * `fallback` is required rather than defaulted: a generic "Something went wrong" on a sign-in screen is
 * useless, and only the caller knows what the user was attempting.
 */
export const getApiErrorMessage = (error: unknown, fallback: string): string => {
  if (isApiError(error) && typeof error.data === 'object' && error.data !== null) {
    const { errorMessage } = error.data as { errorMessage?: unknown };
    if (typeof errorMessage === 'string' && errorMessage !== '') return errorMessage;
  }
  // A network failure surfaces as a TypeError with a useful-enough message; an aborted request never
  // reaches here because the caller drops it.
  if (error instanceof Error && error.message !== '') return error.message;
  return fallback;
};

/**
 * Whether the request failed because the thing is not there, as opposed to the request not getting
 * through.
 *
 * The distinction is user-visible: a 404 is permanent, so offering "Try again" on one sends the reader
 * round a loop that can never succeed. A transient failure is the opposite — retrying is the only
 * useful thing to offer.
 */
export const isNotFoundError = (error: unknown): boolean =>
  isApiError(error) && error.response.status === 404;
